import * as React from "react";

import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";

import { cn } from "lib/cn";

const buttonVariants = cva(
  "inline-flex duration-300 select-none items-center justify-center gap-2 whitespace-nowrap cursor-pointer rounded-full transition-[color,background-color,box-shadow] focus-visible:outline-hidden [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 disabled:bg-neutral-20 disabled:text-white disabled:shadow-none",
  {
    variants: {
      variant: {
        primary:
          "bg-orange-60 hover:bg-orange-70 text-white [&_svg]:fill-white typography-label-thick-30 shadow-xl hover:shadow-2xl shadow-orange-60/50",
        secondary:
          "bg-neutral-100 hover:bg-neutral-110 text-white [&_svg]:fill-white typography-label-thick-30",
        ghost:
          "bg-transparent [&_svg]:fill-neutral-110 typography-label-thick-30",
        link: "typography-label-30",
      },
      size: {
        large: "h-16 px-6",
        regular: "h-12 px-4",
        small: "h-9 px-3",
        icon: "h-[60px] w-[60px]",
        fit: "size-fit",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "large",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  ariaLabel?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      ariaLabel,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";

    const buttonClassName = cn(buttonVariants({ variant, size, className }));

    const isIconButton = size === "icon" || children === undefined;

    return (
      <Comp
        className={buttonClassName}
        ref={ref}
        {...props}
        aria-label={ariaLabel || (isIconButton ? "Button" : undefined)}
      >
        {children}
      </Comp>
    );
  },
);

Button.displayName = "Button";

export { Button, buttonVariants };
